import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import strings from "../global/strings";
import type { Activity } from "./activity.queries";
import type { Bill } from "./bill.queries";

// Mirrors billing_system_BE/controllers/bill.controller.js: getDashboardData's
// pre-seeded fiscalYearSales/fiscalYearSalesCount objects (both keyed by the
// same 12 Nepali month names).
interface FiscalYearMonthlyValues {
	shrawan: number;
	bhadra: number;
	ashoj: number;
	kartik: number;
	mangsir: number;
	poush: number;
	magh: number;
	falgun: number;
	chaitra: number;
	baisakh: number;
	jestha: number;
	ashar: number;
}

// Raw Sequelize sum/count aggregate — count comes back as a string from the
// DB driver, amount is null when no rows match.
interface BillAmountStats {
	totalBillAmount: number | null;
	totalBillCount: number | string;
}

interface CreditNoteAmountStats {
	totalCreditNoteAmount: number | null;
	totalCreditCount: number | string;
}

interface DraftCount {
	draft: number;
	ready: number;
	failed: number;
	total: number;
}

// recentActivityLog (ActivityLog.findAll) uses the identical include set as
// getActivityByCompany (activity.queries.ts's Activity), and recentBills
// (Bill.findAll with a customer_info include excluding createdAt/updatedAt)
// matches bill.queries.ts's Bill exactly — reuse both rather than duplicating.
export interface DashboardData {
	totalBillAmountCurrentFiscalYearByMonth: {
		fiscalYearSales: FiscalYearMonthlyValues;
		fiscalYearSalesCount: FiscalYearMonthlyValues;
	};
	totalBillAmountCurrentFiscalYear: BillAmountStats;
	totalBillAmountPastFiscalYear: BillAmountStats;
	totalCreditNoteAmountCurrentFiscalYear: CreditNoteAmountStats;
	totalCreditNoteAmountPastFiscalYear: CreditNoteAmountStats;
	draftCount: DraftCount;
	recentActivityLog: Activity[];
	recentBills: Bill[];
	totalCustomer: number;
}

// The BE controller never reads req.query (grep-verified) — the FE's old
// ?activityFlag=... param was always a no-op, so this hook takes no params.
async function fetchDashboard(): Promise<DashboardData> {
	const res = await axios.get(`${strings.url}bill/dashboard`, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useDashboard() {
	return useQuery({
		queryKey: ["dashboard"],
		queryFn: fetchDashboard,
	});
}
